import * as Sentry from '@sentry/react';

type ErrorContext = {
  module?: string;
  action?: string;
  metadata?: Record<string, any>;
  level?: Sentry.SeverityLevel;
};

/**
 * Captures an error with Sentry and optionally logs to console in development
 */
export const captureError = (error: unknown, context?: ErrorContext) => {
  const { module, action, metadata, level = 'error' } = context || {};

  // In development, still log to console for debugging
  if (import.meta.env.VITE_ENV === 'local') {
    console.error(error);
  }

  Sentry.withScope((scope) => {
    scope.setLevel(level);

    if (module) {
      scope.setTag('module', module);
    }

    if (action) {
      scope.setTag('action', action);
    }

    if (metadata) {
      scope.setContext('additional', metadata);
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      // If it's not an Error object, capture as a message
      Sentry.captureMessage(String(error), level);
    }
  });
};

/**
 * Captures a message with Sentry (for non-error cases that should still be tracked)
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info',
  metadata?: Record<string, any>,
) => {
  if (import.meta.env.VITE_ENV === 'local') {
    switch (level) {
      case 'error':
      case 'fatal':
        console.error(message);
        break;
      case 'warning':
        console.warn(message);
        break;
      case 'debug':
        console.debug(message);
        break;
      case 'info':
        console.info(message);
        break;
      default:
        console.log(message);
    }
  }

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    if (metadata) {
      scope.setContext('additional', metadata);
    }
    Sentry.captureMessage(message, level);
  });
};
